import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useJoinCohort } from "../../api/CohortsAPI";

const JoinCohort = () => {
  const [nickname, setNickname] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [cohortCode, setCohortCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inputRefs = useRef([]);
  const { currentUser, logout, loading: authLoading } = useAuth();
  const { mutate: joinCohort, isLoading, error: joinError } = useJoinCohort();

  // Initialize refs array and set nickname
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
    if (currentUser?.username) {
      setNickname(currentUser.username);
    }
  }, [currentUser]);

  // Prefill cohort code from URL parameter
  useEffect(() => {
    const codeParam = searchParams.get('code');
    if (codeParam) {
      // Clean and validate the code parameter
      const cleanCode = codeParam.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
      if (cleanCode.length === 6) {
        const codeArray = cleanCode.split('');
        setCohortCode(codeArray);
      }
    }
  }, [searchParams]);

  // Set error from join mutation if it exists
  useEffect(() => {
    if (joinError) {
      const errorMessage = joinError.response?.data?.error || "Failed to join cohort. Please try again.";
      setError(errorMessage);
    }
  }, [joinError]);

  const handleCohortCodeChange = (index, value) => {
    // Only allow alphanumeric characters
    if (value && !/^[A-Z0-9]$/.test(value.toUpperCase())) {
      return;
    }

    const newCohortCode = [...cohortCode];
    newCohortCode[index] = value.toUpperCase();
    setCohortCode(newCohortCode);

    // Auto-focus next input if current input is filled
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === "Backspace" && !cohortCode[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 6);

    if (pastedData) {
      const newCohortCode = [...cohortCode];
      for (let i = 0; i < pastedData.length; i++) {
        if (i < 6) {
          newCohortCode[i] = pastedData[i];
        }
      }
      setCohortCode(newCohortCode);

      // Focus the next empty input or the last input if all filled
      const nextEmptyIndex = newCohortCode.findIndex((char) => !char);
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex].focus();
      } else if (pastedData.length > 0) {
        inputRefs.current[Math.min(pastedData.length - 1, 5)].focus();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const codeString = cohortCode.join("");
    if (codeString.length !== 6) {
      setError("Please enter a complete 6-character cohort code");
      return;
    }

    try {
      setError(null);
      
      joinCohort(
        {
          joinCode: codeString,
          userId: currentUser.id,
          name: sessionName,
        },
        {
          onSuccess: (data) => {
            navigate(`/session/${data.id}/empathize`);
          },
          onError: (error) => {
            console.log("error:", error);
            setError(
              error.response?.data?.error || "Failed to join cohort. Please try again."
            );
          },
        }
      );
    } catch (err) {
      setError(err.response?.data?.error || "Failed to join cohort. Please try again.");
    }
  };

  // Show loading screen while auth is initializing
  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-screen flex-col">
        <h1 className="text-4xl font-extrabold text-center my-6 text-primary">
          Dittrium
        </h1>
        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return (
      <div className="flex justify-center items-center h-screen flex-col">
        <h1 className="text-4xl font-extrabold text-center my-6 text-primary">
          Dittrium
        </h1>
        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-center">Authentication Required</h2>
          <p className="text-gray-600 text-center">
            You need to be logged in to join a cohort.
          </p>
          <div className="space-y-2">
            <Link
              to="/login"
              className="block w-full py-2 px-3 bg-primary text-white text-center rounded-md hover:bg-primary-dark transition duration-200"
            >
              Go to Login
            </Link>
            <Link
              to="/"
              className="block w-full py-2 px-3 bg-gray-200 text-gray-800 text-center rounded-md hover:bg-gray-300 transition duration-200"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const username = currentUser?.username || "User";

  return (
    <div className="flex justify-center items-center h-screen flex-col">
      <h1 className="text-4xl font-extrabold text-center my-6 text-primary">
        Dittrium
      </h1>
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-center">Join Cohort</h2>
        {error && <p className="text-red-500 text-center">{error}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="nickname"
              className="block text-sm font-medium text-gray-700"
            >
              Nickname
            </label>
            <div>
              <input
                type="text"
                id="nickname"
                value={nickname}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                Using your account username
              </p>
            </div>
          </div>
          <div>
            <label
              htmlFor="sessionName"
              className="block text-sm font-medium text-gray-700"
            >
              Session Name
            </label>
            <input
              type="text"
              id="sessionName"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              maxLength={100}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm"
              placeholder="Untitled Cohort Session"
            />
            <p className="text-xs text-gray-500 mt-1">
              Optional. You can rename this session later.
            </p>
          </div>
          <div>
            <label
              htmlFor="cohortCode"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Cohort Code
            </label>
            <div className="flex justify-between gap-2" onPaste={handlePaste}>
              {cohortCode.map((char, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength={1}
                  value={char}
                  onChange={(e) =>
                    handleCohortCodeChange(index, e.target.value)
                  }
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-full aspect-square text-center text-xl font-bold border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary focus:border-primary uppercase"
                  required
                />
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              6 characters, alphanumeric, uppercase only
            </p>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full bg-primary text-white py-2 rounded-lg hover:bg-primary-dark transition duration-300 font-bold ${
              isLoading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Joining..." : "Join Cohort"}
          </button>
        </form>
      </div>

      <div className="fixed bottom-4 right-4 z-50">
        <div
          className="bg-white rounded-lg shadow-xl p-4"
          style={{ width: "220px" }}
        >
          <div className="mb-3">
            <h3 className="font-bold text-gray-800">Account</h3>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Logged in as <span className="font-semibold">{username}</span>
          </p>
          <div className="space-y-2">
            <Link
              to="/"
              className="block w-full py-2 px-3 bg-primary text-white text-center rounded-md hover:bg-primary-dark transition duration-200"
            >
              Dashboard
            </Link>
            <button
              onClick={logout}
              className="block w-full py-2 px-3 bg-gray-200 text-gray-800 text-center rounded-md hover:bg-gray-300 transition duration-200"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinCohort;
